/**
 * 根据 externalPlaying 与 playing 同步：外部 true 时执行 play + updatePlaying + 进度/历史追踪，外部 false 时 pause + 停止追踪
 * 抽离自 MusicPlayer.js
 */
import { useEffect } from 'react';

/**
 * @param {object} options
 * @param {boolean|null} options.externalPlaying
 * @param {boolean} options.playing
 * @param {object|null} options.currentMusic
 * @param {React.MutableRefObject<boolean>} options.isLoadingRef
 * @param {function(object): string|null} options.getVideoFilePath
 * @param {function(boolean, React.RefObject, React.RefObject): HTMLMediaElement|null} options.getActiveMediaElement
 * @param {React.RefObject} options.videoRef
 * @param {React.RefObject} options.audioRef
 * @param {function(boolean): void} options.updatePlaying
 * @param {function(): void} options.resumeAudioContext
 * @param {React.MutableRefObject<boolean>} options.isProgressTrackingRef
 * @param {React.MutableRefObject<ReturnType<typeof setInterval>|null>} options.playHistoryIntervalRef
 * @param {function(): void} options.startProgressTracking
 * @param {function(): void} options.startPlayHistoryTracking
 * @param {function(): Promise<void>} options.recordPlay
 * @param {function(boolean): void} options.applyAudioFocusMode
 * @param {function(): void} options.stopProgressTracking
 * @param {function(): void} options.stopPlayHistoryTracking
 */
export function useExternalPlayingSync({
  externalPlaying,
  currentMusic,
  playing,
  isLoadingRef,
  getVideoFilePath,
  getActiveMediaElement,
  videoRef,
  audioRef,
  updatePlaying,
  resumeAudioContext,
  isProgressTrackingRef,
  playHistoryIntervalRef,
  startProgressTracking,
  startPlayHistoryTracking,
  recordPlay,
  applyAudioFocusMode,
  stopProgressTracking,
  stopPlayHistoryTracking
}) {
  useEffect(() => {
    if (externalPlaying !== null && externalPlaying !== playing) {
      if (externalPlaying && !playing) {
        if (isLoadingRef.current) return;
        const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
        const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
        if (mediaEl && currentMusic) {
          if (mediaEl.readyState >= 2) {
            resumeAudioContext().then(() => {}).catch(() => {});
            const playPromise = mediaEl.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  updatePlaying(true);
                  if (!isProgressTrackingRef.current) startProgressTracking();
                  if (playHistoryIntervalRef.current === null) startPlayHistoryTracking();
                  recordPlay();
                  if ('mediaSession' in navigator) applyAudioFocusMode(true);
                })
                .catch(err => {
                  if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
                    console.error('Auto play error:', err);
                  }
                });
            }
          } else {
            const onLoadedMetadata = () => {
              try { mediaEl.removeEventListener('loadedmetadata', onLoadedMetadata); } catch (_) {}
              resumeAudioContext().then(() => {}).catch(() => {});
              const playPromise = mediaEl.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    updatePlaying(true);
                    if (!isProgressTrackingRef.current) startProgressTracking();
                    if (playHistoryIntervalRef.current === null) startPlayHistoryTracking();
                    recordPlay();
                    if ('mediaSession' in navigator) applyAudioFocusMode(true);
                  })
                  .catch(err => {
                    if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
                      console.error('Auto play error:', err);
                    }
                  });
              }
            };
            mediaEl.addEventListener('loadedmetadata', onLoadedMetadata);
          }
        }
      } else if (!externalPlaying && playing) {
        const hasVideo = currentMusic ? !!getVideoFilePath(currentMusic) : false;
        const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
        if (mediaEl) {
          mediaEl.pause();
          updatePlaying(false);
          stopProgressTracking();
          stopPlayHistoryTracking();
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPlaying, currentMusic, playing]);
}
